import { useState } from 'react';
import type { TurnoDeAgenda } from '@medchoice/shared';
import { consultorio } from '../../api/consultorio';
import { usePedido } from '../../api/usePedido';
import { irA } from '../../router';
import {
  AvisoError, Avatar, Boton, Cargando, Entrada, EstadoVacio, FalloDeCarga, Icono, Insignia, InsigniaEstadoTurno, Kpi,
  Pestanas, Selector, Tarjeta,
} from '../../ui';
import { SeccionPanel } from '../../ui/layout/PaginaPanel';
import { fechaCorta, hoyIso, pesos, sumarDias } from '../../ui/format';
import { estaCerrado, nombreDe } from './piezas';

type Filtro = 'todos' | 'atendidos' | 'ausentes' | 'proximos' | 'cancelados';

const PERIODOS = [
  { valor: '30', texto: 'Últimos 30 días' }, { valor: '90', texto: 'Últimos 90 días' },
  { valor: '365', texto: 'Último año' }, { valor: 'todo', texto: 'Todo el historial' },
];

function filtrar(turnos: TurnoDeAgenda[], hoy: string): Record<Filtro, TurnoDeAgenda[]> {
  return {
    todos: turnos,
    atendidos: turnos.filter((t) => t.estado === 'atendido'),
    ausentes: turnos.filter((t) => t.estado === 'ausente'),
    proximos: turnos.filter((t) => t.fecha >= hoy && (t.estado === 'pendiente' || t.estado === 'confirmado')),
    cancelados: turnos.filter((t) => t.estado === 'cancelado'),
  };
}

export function Turnos() {
  const [periodo, setPeriodo] = useState('90');
  const [filtro, setFiltro] = useState<Filtro>('todos');
  const [busqueda, setBusqueda] = useState('');
  const desde = periodo === 'todo' ? undefined : sumarDias(hoyIso(), -Number(periodo));
  const pedido = usePedido(() => consultorio.turnos(desde), [desde]);

  return (
    <SeccionPanel titulo="Turnos"
      descripcion={periodo === 'todo' ? 'Todo tu historial' : `Tu historial de los últimos ${periodo} días`}
      acciones={
        <div className="accion-selector">
          <Selector aria-label="Período" value={periodo} opciones={PERIODOS} onChange={(e) => setPeriodo(e.target.value)} />
        </div>
      }>
      {pedido.estado === 'cargando' && !pedido.datos && <Cargando />}
      {pedido.estado === 'error' && <FalloDeCarga mensaje={pedido.error ?? ''} alReintentar={pedido.recargar} />}
      {pedido.datos && (
        <Listado turnos={pedido.datos} filtro={filtro} alFiltrar={setFiltro} busqueda={busqueda} alBuscar={setBusqueda} />
      )}
    </SeccionPanel>
  );
}

interface PropsListado {
  turnos: TurnoDeAgenda[];
  filtro: Filtro;
  alFiltrar: (f: Filtro) => void;
  busqueda: string;
  alBuscar: (texto: string) => void;
}

function Listado({ turnos, filtro, alFiltrar, busqueda, alBuscar }: PropsListado) {
  const porFiltro = filtrar(turnos, hoyIso());
  const cerrados = porFiltro.atendidos.length + porFiltro.ausentes.length;
  const sinCalificar = turnos.filter((t) => estaCerrado(t) && !t.calificado).length;
  const texto = busqueda.trim().toLowerCase();
  const lista = porFiltro[filtro].filter((t) =>
    !texto || nombreDe(t.paciente).toLowerCase().includes(texto) || t.codigo.toLowerCase().includes(texto));

  return (
    <div className="pila">
      <div className="kpis">
        <Kpi etiqueta="Atendidos" valor={porFiltro.atendidos.length} icono="check-circle" />
        <Kpi etiqueta="No asistieron" valor={porFiltro.ausentes.length} icono="user-x"
          pista={cerrados ? `${Math.round((porFiltro.ausentes.length / cerrados) * 100)}% de los cerrados` : undefined} />
        <Kpi etiqueta="Facturado" valor={pesos(porFiltro.atendidos.reduce((a, t) => a + t.precio, 0))} icono="wallet" />
        <Kpi etiqueta="Próximos" valor={porFiltro.proximos.length} icono="calendar" />
      </div>

      {sinCalificar > 0 && (
        <AvisoError tono="aviso">
          Te {sinCalificar === 1 ? 'queda 1 turno' : `quedan ${sinCalificar} turnos`} sin calificar al paciente. Es lo que le
          sirve al próximo profesional para saber con quién se compromete.
        </AvisoError>
      )}

      <Tarjeta>
        <Pestanas<Filtro> etiqueta="Estado del turno" activa={filtro} alElegir={alFiltrar} pestanas={[
          { id: 'todos', texto: 'Todos', cantidad: porFiltro.todos.length },
          { id: 'atendidos', texto: 'Atendidos', cantidad: porFiltro.atendidos.length },
          { id: 'ausentes', texto: 'No asistieron', cantidad: porFiltro.ausentes.length },
          { id: 'proximos', texto: 'Próximos', cantidad: porFiltro.proximos.length },
          { id: 'cancelados', texto: 'Cancelados', cantidad: porFiltro.cancelados.length },
        ]} />
        <div className="listado">
          <Entrada aria-label="Buscar" icono="search" placeholder="Buscar por paciente o por código de turno"
            maxLength={50} value={busqueda} onChange={(e) => alBuscar(e.target.value)} />
          {lista.length === 0 ? (
            <EstadoVacio icono="calendar" titulo="Sin turnos"
              cuerpo={texto ? `Ninguno coincide con “${busqueda}”.` : 'No hay turnos en este período con ese estado.'}
              accion={texto ? <Boton variante="secundario" onClick={() => alBuscar('')}>Limpiar la búsqueda</Boton> : undefined} />
          ) : lista.map((t) => (
            <button key={t.id_turno} type="button" className="fila-turno" onClick={() => irA(`/pro/turno/${t.id_turno}`)}>
              <span className="fila-turno__hora">{fechaCorta(t.fecha)}<br />{t.hora_inicio}</span>
              <Avatar nombre={nombreDe(t.paciente)} tamano={34} decorativo />
              <span className="fila-turno__datos">
                <strong>{nombreDe(t.paciente)}</strong>
                <span>{t.servicio} · {pesos(t.precio)}</span>
              </span>
              {estaCerrado(t) && !t.calificado && <Insignia tono="coral" tamano="chico">Sin calificar</Insignia>}
              <InsigniaEstadoTurno estado={t.estado} />
              <Icono nombre="chevron-right" tamano={17} />
            </button>
          ))}
        </div>
      </Tarjeta>
    </div>
  );
}
